import React, { ReactElement, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { button, header1, header6, plainTextStyle } from '@westwing/ui-kit/typography';
import { forTablet, forDesktop } from '@westwing/ui-kit/breakPoints';
import { SpinnerScheme, InlineSpinner } from '@westwing/ui-kit/Spinner';
import { FormattedInfusionData, FormattedInfusionDataItems } from '../../../redux/infusions/types';
import useGa from '../../../hooks/useGa';
import { INTERSECTION_ACTION, LOADED_ACTION } from '../../../hooks/lazyImages/types';
import useElementIntersection from '../../../hooks/useElementIntersection';
import useLazyImageReducer from '../../../hooks/lazyImages';
import { lazyImageAction } from '../../../hooks/lazyImages/actions';
import { gridItemWidth } from '../ProductGrid/ProductGrid';
import ErrorBoundary from '../../Generic/ErrorBoundaries';
import HorizontalScrollableElement from '../../ScrollableElement';
import scroll from '../../GlobalStyles/scroll';
import { textLinesLimitEllipsisCss } from 'Client/components/Generic/Text/StyledText';

interface InfusionProps {
    infusions: FormattedInfusionData;
}

const InfusionContainer = styled.div`
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal20};
    border-top: 1px solid ${({ theme }) => theme.colors.charcoal20};
    margin: 25px -10px 10px;
    grid-row: 3 / 4;
    grid-column: 1 / -1;
    ${forTablet`
        margin: 25px 0 25px;
    `}
    ${forDesktop`
        margin: 30px 0 30px;
    `}
`;

const StyledPreTitle = styled.div`
    ${plainTextStyle}
    color: ${({ theme }) => theme.colors.charcoal100};
    max-height: 20px;
    max-width: 155px;
    overflow: hidden;
`;

const StyledTopicTitle = styled.div`
    ${header6}
    color: ${({ theme }) => theme.colors.charcoal100};
    ${textLinesLimitEllipsisCss(2)}
    ${forTablet`
        ${header1}
    `}
`;

const StyledCTAText = styled.div`
    ${button}
    color: ${({ theme }) => theme.colors.charcoal100};
    line-height: 1.4;
    text-decoration: underline;
`;

export const HorizontalScrollContainer = styled.div`
    padding: 0px 0 35px 40px;
    display: flex;
    flex-direction: row;
    scroll-behavior: smooth;
    max-width: 100vw;
    ${scroll('x', true)}
    ${forTablet`
        padding: 0 0 37px;
    `}
`;

const InfusionItemInfoBox = styled.div`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    ${forTablet`
        margin-top: 10px;
    `}
`;

const InfusionImage = styled.img`
    object-fit: cover;
    width: 100%;
    height: 100%;
`;

const InfusionImageWrapper = styled.div`
    height: calc(${gridItemWidth} * (233 / 175));
    min-width: 100px;
    background: rgba(0, 0, 0, 0.03);
    ${forTablet`
        height: auto;
    `}
`;

const LinkedContainer = styled.a`
    padding-right: 10px;
    flex: 1 0 185px;
    ${forTablet`
        padding-right: 0;
        &:not(:first-child) {
            margin-left: 10px;
            padding-right: 0;
        }
        flex-basis: 32.7%;
    `}
    text-decoration: none;
`;

const Spinner = styled(InlineSpinner)`
    position: relative;
    z-index: ${({ theme }) => theme.zIndexProductSpinner};
`;

const intersectionOptions = { rootMargin: '0px 0px 50px 0px' };

interface ImageWrapperProps {
    infusion: FormattedInfusionDataItems;
    trackingLabel: string;
}

const ImageWrapper = ({ infusion, trackingLabel }: ImageWrapperProps): ReactElement => {
    const gaTracking = useGa();

    const infusionImageElement = useRef<HTMLDivElement>(null);
    const [state, dispatch] = useLazyImageReducer({
        isLoaded: false,
        hasComeIntoView: false,
        showSpinner: false,
    });

    // Callback for when the image is loaded
    const setIsLoaded = useCallback((loaded: boolean): void => dispatch(lazyImageAction(LOADED_ACTION, loaded)), [
        dispatch,
    ]);

    const { isLoaded, hasComeIntoView, showSpinner } = state;

    useElementIntersection(
        infusionImageElement,
        () => {
            dispatch(lazyImageAction(INTERSECTION_ACTION, true));
            gaTracking.trackUsers({
                action: gaTracking.actions.Users.ContentInfusionNavigationImpression,
                label: trackingLabel,
            });
        },
        intersectionOptions
    );

    return (
        <InfusionImageWrapper ref={infusionImageElement}>
            {showSpinner && !isLoaded && <Spinner scheme={SpinnerScheme.light} />}
            <InfusionImage
                src={hasComeIntoView ? infusion.itemImage : undefined}
                alt={infusion.itemTopicTitle}
                onLoad={() => setIsLoaded(true)}
            />
        </InfusionImageWrapper>
    );
};

const Infusions = ({ infusions }: InfusionProps): ReactElement => {
    const { mainTitle, items } = infusions;
    const gaTracking = useGa();

    const handleClick = (topicTitle: string, currentCount: Number) => {
        gaTracking.trackUsers({
            action: gaTracking.actions.Users.ContentInfusionNavigation,
            label: `${topicTitle} ${currentCount}/${items ? items.length : 0}`,
        });
    };

    const showNavArrows = (items && items.length > 3) as boolean;

    return (
        <ErrorBoundary boundaryName="Infusions" fallbackComponent={null}>
            <InfusionContainer>
                <HorizontalScrollableElement
                    title={mainTitle}
                    showHeader
                    showNavArrows={showNavArrows}
                    centerOffset={100}
                >
                    <HorizontalScrollContainer>
                        {items &&
                            items.map((infusion, index) => {
                                return (
                                    <LinkedContainer
                                        key={infusion.itemTopicTitle}
                                        href={infusion.itemLink}
                                        onClick={() => handleClick(infusion.itemTopicTitle, index + 1)}
                                    >
                                        <ImageWrapper
                                            infusion={infusion}
                                            trackingLabel={`${infusion.itemTopicTitle} ${index + 1}/${
                                                items ? items.length : 0
                                            }`}
                                        />
                                        <InfusionItemInfoBox>
                                            <StyledPreTitle>{infusion.itemPreTitle}</StyledPreTitle>
                                            <StyledTopicTitle>{infusion.itemTopicTitle}</StyledTopicTitle>
                                            <StyledCTAText>{infusion.itemCtaText}</StyledCTAText>
                                        </InfusionItemInfoBox>
                                    </LinkedContainer>
                                );
                            })}
                    </HorizontalScrollContainer>
                </HorizontalScrollableElement>
            </InfusionContainer>
        </ErrorBoundary>
    );
};

export default Infusions;
